import {config} from '@/profile-creation-wizard/config/applicationConfig';
import {CreationStepName, ProfileCreationStepGroup} from '@/profile-creation-wizard/config/steps/ProfileCreationSteps';
import maltClassicRoutesConfig from '@/profile-creation-wizard/router/config/MaltClassicRoutesConfiguration';
import maltStrategyRoutesConfiguration from '@/profile-creation-wizard/router/config/MaltStrategyRoutesConfiguration';
import ProfileCreationRouterHelper from '@/profile-creation-wizard/router/ProfileCreationRouterHelper';
import {createRouter, createWebHistory, NavigationGuardNext, RouteLocationNormalized} from 'vue-router';
import store from '@/profile-creation-wizard/store';
import actions from '@/profile-creation-wizard/store/completion/actions';
import completionGetters from '@/profile-creation-wizard/store/completion/getters';
import {ProfileCreationStepNavigationTrackingEvent, trackStepsNavigation} from '../tracking/RouterNavigationTracker';
import mutations from '@/profile-creation-wizard/store/creation/mutations';

const routeAliasesForCompletionStep = ['/completion', '/completion/strategy'];
const routeAliasesForCreationStep = ['/creation', '/creation/strategy', '/signup', '/signup/strategy'];

const routesConfig = getRoutesConfig();

const router = createRouter({
    history: createWebHistory(config.baseUrl),
    linkActiveClass: 'active',
    routes: routesConfig.routes,
});

const funnelData = 'funnelData';
const skipRedirect = 'SKIP_REDIRECT';

// noinspection TypeScriptValidateJSTypes
router.beforeEach(async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const routeQuery = to.query || {};

    ProfileCreationRouterHelper.handleInvitationCode(routeQuery);

    const existingSignupData = sessionStorage.getItem(funnelData);
    const shouldSkipRedirect = !!sessionStorage.getItem(skipRedirect);

    if (existingSignupData) {
        const existingData = JSON.parse(`${existingSignupData}`);
        store.commit('UPDATE_HEADLINE', existingData.headline);
        if (existingData.isCategorizationInputManual) {
            store.dispatch('IMPORT_MANUAL_FAMILIES_CATEGORIES', existingData.categorizationData);
        }
        store.commit('SELECT_EXPERIENCE_LEVEL', existingData.experienceLevel);
        sessionStorage.removeItem(funnelData);
        sessionStorage.setItem(skipRedirect, 'true');
        next({name: existingData.currentStep});
        return;
    }

    const funnelModeChanged = to.meta.funnelMode !== store.state.funnelMode;
    const isFunnelStrategy = ProfileCreationRouterHelper.isFunnelModeStrategyActivated(to.path);

    const isGoingToCompletionStep = ProfileCreationRouterHelper.isGoingToAStepOfGroup(ProfileCreationStepGroup.COMPLETION, to);
    const redirectToCompletionFirstStep = ProfileCreationRouterHelper.needsRedirectionToFirstStepOfGroup(
        routesConfig,
        ProfileCreationStepGroup.COMPLETION,
        to,
        from,
    );

    const forceDisplayOfAccountStep = to.name === CreationStepName.SIGNUP_ACCOUNT_CREATION && to.query['r'];
    if (forceDisplayOfAccountStep) {
        const profileCreationRequestId = to.query['r'];
        store.commit(mutations.updateProfileCreationResumptionDetails, {
            requestId: profileCreationRequestId,
            resumptionUrl: `/freelancer-signup/resumption/${profileCreationRequestId}`,
        });
    }

    const redirectToSignupFirstStep =
        !forceDisplayOfAccountStep &&
        ProfileCreationRouterHelper.needsRedirectionToFirstStepOfGroup(routesConfig, ProfileCreationStepGroup.SIGNUP, to, from);

    const noPreviousRoute = ProfileCreationRouterHelper.hasNoPreviousRoute(from);

    const isInSignupAlias = ProfileCreationRouterHelper.routeIsAnAlias(to, routeAliasesForCreationStep) && !to.name;
    const isInCompletionAlias = ProfileCreationRouterHelper.routeIsAnAlias(to, routeAliasesForCompletionStep) && !to.name;

    if (shouldSkipRedirect) {
        sessionStorage.removeItem(skipRedirect);
        return next();
    }

    if (funnelModeChanged) {
        store.commit('setFunnelMode', to.meta.funnelMode);
    }

    if (redirectToSignupFirstStep || isInSignupAlias) {
        return next({name: routesConfig.creationFirstStep, query: routeQuery, replace: true});
    }

    if (isFunnelStrategy) {
        if ((isGoingToCompletionStep && noPreviousRoute) || isInCompletionAlias) {
            const currentStepName = await store.dispatch(actions.fetchCurrentStep, routesConfig.completionFirstStep);
            if (currentStepName !== to.name) {
                return next({name: currentStepName, query: routeQuery});
            }
        }
    } else if (redirectToCompletionFirstStep || isInCompletionAlias) {
        return next({name: routesConfig.completionFirstStep, query: routeQuery, replace: true});
    }
    next();
});

router.afterEach((to: RouteLocationNormalized, from: RouteLocationNormalized) => {
    const isFunnelStrategy = ProfileCreationRouterHelper.isFunnelModeStrategyActivated(to.path);
    const isGoingToStepOfGroupCompletion = ProfileCreationRouterHelper.isGoingToAStepOfGroup(ProfileCreationStepGroup.COMPLETION, to);
    const isGoingToFirstStepOfGroupSignup = ProfileCreationRouterHelper.isGoingToFirstStepOfGroup(routesConfig, ProfileCreationStepGroup.SIGNUP, to);

    if (isGoingToFirstStepOfGroupSignup && !!to.query.corporateProgram) {
        store.commit(mutations.updateCorporateProgram, to.query.corporateProgram);
    }

    if (isGoingToStepOfGroupCompletion) {
        if (isFunnelStrategy) {
            store.dispatch(actions.updateCurrentStep, to.name);
        }

        if (store.getters[completionGetters.hasLoadedAllData] === false) {
            store.dispatch(actions.fetchCompletionFormData);
        }
    }

    const stepGroup = to.meta.stepGroup as ProfileCreationStepGroup;
    const event: ProfileCreationStepNavigationTrackingEvent = {
        stepName: (to.meta.trackingStepName as string).toLowerCase(),
        stepNumber: to.meta.stepNumber as string,
        funnelModeName: (to.meta.funnelMode as string).toLowerCase(),
        isGoingToFirstStepOfItsGroup: ProfileCreationRouterHelper.isGoingToFirstStepOfGroup(routesConfig, stepGroup, to) && !from.name,
        group: stepGroup,
        isRepatriatedFreelancer: !!to.query.repatriationCompanyName,
    };

    if (to.name != from.name) {
        trackStepsNavigation(event);
    }
});

function getRoutesConfig() {
    const isConnected = window.pageContext.isConnected as boolean;
    // New account creation step should be available only with FF and if user is not already connected as client
    const withAccountCreation = !isConnected;

    if (ProfileCreationRouterHelper.needStrategyConfiguration(window.location.pathname)) {
        return maltStrategyRoutesConfiguration(withAccountCreation);
    }
    return maltClassicRoutesConfig(withAccountCreation);
}

export default router;
